import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DespachoInfoCallout,
  DespachoSectionCard,
  DespachoSimpleTable,
  DespachoStatusBadge,
} from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";

const ALERT_STATUS_LABELS = {
  pending: "Pendiente",
  sent: "Enviada",
  acknowledged: "Atendida",
  cancelled: "Cancelada",
  resolved: "Resuelta",
  skipped_duplicate: "Duplicada",
};

function groupAlertsByBucket(items, today) {
  const buckets = { hoy: [], proximos: [], vencidos: [] };
  for (const row of items) {
    const d = String(row.scheduled_for || "").slice(0, 10);
    if (!d) continue;
    if (d < today) buckets.vencidos.push(row);
    else if (d === today) buckets.hoy.push(row);
    else buckets.proximos.push(row);
  }
  return buckets;
}

export function RemindersSummaryCards({ onFilter, className = "" }) {
  const [summary, setSummary] = useState(null);
  const [loadState, setLoadState] = useState("loading");

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const data = await despachoFiscalInfoApi.getRemindersSummary();
      setSummary(data);
      setLoadState("ready");
    } catch {
      setSummary(null);
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cardDefs = [
    { key: "overdue_tasks", label: "Tareas vencidas", filter: "overdue_tasks" },
    { key: "overdue_instances", label: "Obligaciones vencidas", filter: "overdue_instances" },
    { key: "pending_alerts", label: "Alertas pendientes", filter: "pending_alerts" },
    { key: "escalated_alerts", label: "Escaladas supervisor", filter: "escalated" },
    { key: "suggested_drafts_pending", label: "Mensajes sugeridos", filter: "drafts" },
    { key: "reminders_sent_today", label: "Recordatorios hoy", filter: "sent_today" },
  ];

  if (loadState === "loading") {
    return (
      <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 ${className}`} data-testid="reminders-summary-cards">
        {cardDefs.map((c) => (
          <div key={c.key} className="rounded-sm border border-slate-200 bg-white px-3 py-2">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 block">{c.label}</span>
            <Loader2 className="h-5 w-5 animate-spin text-slate-400 mt-1" aria-hidden />
          </div>
        ))}
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className={`rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 ${className}`} data-testid="reminders-summary-cards">
        <p className="m-0 mb-2">No se pudo cargar el resumen de recordatorios.</p>
        <Button type="button" size="sm" variant="outline" className="rounded-sm h-7" onClick={load}>
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 ${className}`} data-testid="reminders-summary-cards">
      {cardDefs.map((c) => (
        <button
          key={c.key}
          type="button"
          className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-left hover:bg-slate-50 transition-colors"
          onClick={() => onFilter?.(c.filter)}
        >
          <span className="text-[10px] uppercase tracking-wide text-slate-500 block">{c.label}</span>
          <strong className="text-lg text-[#0F2D5C]">{summary?.[c.key] ?? 0}</strong>
        </button>
      ))}
    </div>
  );
}

export function RemindersGeneratePanel({ readOnly = false, onGenerated, autoPreview = true }) {
  const [preview, setPreview] = useState(null);
  const [previewState, setPreviewState] = useState("idle");
  const [busy, setBusy] = useState(false);

  const runPreview = useCallback(async () => {
    setPreviewState("loading");
    setPreview(null);
    try {
      const data = await despachoFiscalInfoApi.previewGenerateReminders();
      setPreview(data);
      setPreviewState("ready");
    } catch (err) {
      setPreviewState("error");
      toast.error(parseFiscalInfoError(err).message);
    }
  }, []);

  useEffect(() => {
    if (autoPreview) {
      runPreview();
    }
  }, [autoPreview, runPreview]);

  const confirmGenerate = async () => {
    if (readOnly) return;
    setBusy(true);
    try {
      const result = await despachoFiscalInfoApi.generateReminders({ confirm: true });
      toast.success(
        `Alertas: ${result.alerts_created ?? 0}, borradores cliente: ${result.drafts_created ?? 0}.`,
      );
      setPreview(null);
      setPreviewState("idle");
      onGenerated?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const sendPending = async () => {
    if (readOnly) return;
    setBusy(true);
    try {
      const result = await despachoFiscalInfoApi.sendPendingReminders();
      toast.success(`Se enviaron ${result.sent ?? 0} recordatorio(s) al chat interno.`);
      onGenerated?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DespachoSectionCard
      title="Recordatorios automáticos"
      description="Alertas al chat interno y borradores sugeridos a clientes según reglas del Calendario."
      testId="reminders-generate-panel"
    >
      <div className="space-y-4">
      <DespachoInfoCallout>
        Genera alertas desde reglas activas (instancias y tareas abiertas). Solo se agrupan empresas del{" "}
        <strong>mismo auxiliar asignado</strong> cuando comparten obligación y fecha de vencimiento — un mensaje
        por auxiliar al chat interno. Los mensajes a clientes quedan como borradores sugeridos — sin envío
        automático; la bandeja de revisión está en{" "}
        <strong>Calendario → Recordatorios a Clientes → Bandeja de envío</strong>. El tablero de alertas
        precargadas está en <strong>Calendario → Recordatorios programados</strong>. Con el daemon{" "}
        <code className="text-[10px]">fiscal_reminders_scheduler.py</code> el envío al chat puede ser automático
        cada hora.
      </DespachoInfoCallout>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" className="rounded-sm" onClick={runPreview} disabled={busy}>
          Vista previa
        </Button>
        {!readOnly ? (
          <>
            <Button
              type="button"
              className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]"
              onClick={confirmGenerate}
              disabled={busy || previewState !== "ready"}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden /> : null}
              Confirmar generación
            </Button>
            <Button type="button" variant="secondary" className="rounded-sm" onClick={sendPending} disabled={busy}>
              Enviar pendientes al chat
            </Button>
          </>
        ) : null}
      </div>
      {previewState === "loading" ? (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Calculando…
        </div>
      ) : null}
      {previewState === "error" ? (
        <p className="text-sm text-amber-800 m-0">
          No se pudo calcular la vista previa. Verifique que existan vencimientos con fecha fiscal y auxiliar asignado.
        </p>
      ) : null}
      {previewState === "ready" && preview && !(preview.alerts_to_create || []).length && !(preview.drafts_to_create || []).length ? (
        <p className="text-sm text-slate-600 m-0">
          No hay recordatorios nuevos en este momento. Necesita instancias fiscales abiertas con{" "}
          <strong>fecha de vencimiento</strong> dentro de la ventana de las reglas (p. ej. −5, −2, 0 y +1 día).
          Genere vencimientos en el panel superior y confirme fechas en Seguimiento de tareas.
        </p>
      ) : null}
      {preview ? (
        <DespachoSectionCard
          title="Vista previa"
          description={`${preview.summary?.new_alerts ?? 0} alerta(s) nueva(s), ${preview.summary?.new_drafts ?? 0} borrador(es), ${preview.summary?.duplicate_count ?? 0} duplicado(s) omitido(s).`}
        >
          <DespachoSimpleTable
            columns={[
              { key: "scheduled_for", label: "Fecha" },
              { key: "alert_type", label: "Tipo" },
              { key: "message", label: "Mensaje", render: (r) => (r.message || r.body || "—").slice(0, 80) },
            ]}
            rows={[
              ...(preview.alerts_to_create || []).map((a, i) => ({ ...a, id: `a-${i}` })),
              ...(preview.drafts_to_create || []).map((d, i) => ({
                ...d,
                id: `d-${i}`,
                alert_type: "client_message_suggestion",
              })),
            ]}
            emptyLabel="Nada nuevo que generar."
          />
        </DespachoSectionCard>
      ) : null}
      </div>
    </DespachoSectionCard>
  );
}

export function FiscalAlertsListPanel({ statusFilter, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const params = {};
      if (statusFilter === "pending_alerts") params.status = "pending";
      if (statusFilter === "escalated") params.alert_type = "supervisor_escalation";
      const rows = await despachoFiscalInfoApi.listFiscalAlerts(params);
      setItems(rows);
      setLoadState("ready");
    } catch {
      setItems([]);
      setLoadState("error");
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const buckets = groupAlertsByBucket(items, today);

  const acknowledge = async (alertId) => {
    if (readOnly) return;
    try {
      await despachoFiscalInfoApi.patchFiscalAlert(alertId, { status: "acknowledged" });
      toast.success("Alerta marcada como atendida.");
      await load();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    }
  };

  const renderBucket = (title, rows) =>
    rows.length ? (
      <DespachoSectionCard key={title} title={title} description={`${rows.length} alerta(s)`}>
        <DespachoSimpleTable
          columns={[
            { key: "scheduled_for", label: "Programada" },
            { key: "alert_type", label: "Tipo" },
            {
              key: "status",
              label: "Estado",
              render: (r) => (
                <DespachoStatusBadge variant="secondary">
                  {ALERT_STATUS_LABELS[r.status] || r.status}
                </DespachoStatusBadge>
              ),
            },
            { key: "message", label: "Mensaje", render: (r) => (r.message || "—").slice(0, 100) },
            {
              key: "_act",
              label: "Acción",
              render: (r) =>
                !readOnly && r.status === "pending" ? (
                  <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => acknowledge(r.id)}>
                    Atendida
                  </Button>
                ) : null,
            },
          ]}
          rows={rows.map((r) => ({ ...r, id: r.id }))}
          emptyLabel="Sin alertas."
        />
      </DespachoSectionCard>
    ) : null;

  if (loadState === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando alertas…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="fiscal-alerts-list">
      {renderBucket("Hoy", buckets.hoy)}
      {renderBucket("Próximos 7 días", buckets.proximos.slice(0, 50))}
      {renderBucket("Vencidos", buckets.vencidos)}
      {!buckets.hoy.length && !buckets.proximos.length && !buckets.vencidos.length ? (
        <DespachoSectionCard title="Sin alertas">
          <p className="text-sm text-slate-600 m-0">No hay alertas con los filtros actuales.</p>
        </DespachoSectionCard>
      ) : null}
    </div>
  );
}

export function SuggestedClientMessagesPanel({ readOnly = false }) {
  const [drafts, setDrafts] = useState([]);
  const [loadState, setLoadState] = useState("loading");

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const rows = await despachoFiscalInfoApi.listClientMessageDrafts();
      const suggested = rows.filter((d) =>
        ["generated", "pending", "borrador"].includes(String(d.status || "").toLowerCase()),
      );
      setDrafts(suggested);
      setLoadState("ready");
    } catch {
      setDrafts([]);
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copyBody = async (draft) => {
    try {
      await navigator.clipboard.writeText(draft.body || "");
      if (!readOnly) {
        await despachoFiscalInfoApi.patchClientMessageDraft(draft.id, { status: "copied" });
      }
      toast.success("Mensaje copiado al portapapeles.");
      await load();
    } catch {
      toast.error("No se pudo copiar el mensaje.");
    }
  };

  const markSent = async (draftId) => {
    if (readOnly) return;
    try {
      await despachoFiscalInfoApi.patchClientMessageDraft(draftId, { status: "sent_manually" });
      toast.success("Marcado como enviado manualmente.");
      await load();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    }
  };

  if (loadState === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando mensajes sugeridos…
      </div>
    );
  }

  return (
    <DespachoSectionCard
      title="Mensajes sugeridos a clientes"
      description="Borradores generados automáticamente. Copie y envíe manualmente por WhatsApp o correo."
      testId="suggested-client-messages"
    >
      {!drafts.length ? (
        <p className="text-sm text-slate-600 m-0">No hay borradores pendientes.</p>
      ) : (
        <ul className="space-y-3 m-0 p-0 list-none">
          {drafts.map((d) => (
            <li key={d.id} className="rounded-sm border border-slate-200 bg-white p-3 text-sm">
              <div className="flex flex-wrap justify-between gap-2 mb-2">
                <span className="font-medium text-[#0F2D5C]">
                  {d.subject || d.message_type || "Mensaje sugerido"}
                </span>
                <span className="text-xs text-slate-500">{d.period || d.fiscal_client_id}</span>
              </div>
              <p className="text-slate-700 whitespace-pre-wrap m-0 mb-2">{d.body}</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" className="rounded-sm h-7" onClick={() => copyBody(d)}>
                  Copiar
                </Button>
                {!readOnly ? (
                  <Button type="button" size="sm" variant="secondary" className="rounded-sm h-7" onClick={() => markSent(d.id)}>
                    Marcar enviado manualmente
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </DespachoSectionCard>
  );
}
